<script setup lang="ts">
import { computedAsync } from '@vueuse/core';
import Image from 'primevue/image';
import { computed, onMounted, onUnmounted, shallowRef, watch } from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import mergePropsWithDefaults from 'lib/utils/mergePropsWithDefaults.util';

import { ImageProps } from './Image.vue.d';
import Button from '../button/Button.vue';
import Icon from '../icon/Icon.vue';

const props = withDefaults(defineProps<ImageProps>(), {
  preview: true,
  useDefaultSize: true,
});

const { defaultProps } = useWangsVueConfig('Image');
const config = computed(() => mergePropsWithDefaults(props, defaultProps));

onMounted(() => {
  setTimeout(() => {
    startInterval();
  }, 3000);
});

onUnmounted(() => {
  stopInterval();

  if (
    typeof imageThumbnail.value == 'string' &&
    imageThumbnail.value.includes('blob')
  )
    URL.revokeObjectURL(imageThumbnail.value);

  for (const url of objectURLs) URL.revokeObjectURL(url);
});

const currentGalleryImage = shallowRef<number>(0);
const intervalId = shallowRef<number>();
const timeOutId = shallowRef<number>();
const errorLoadImage = shallowRef<boolean>(false);

const objectURLs: string[] = []; // Stores objectURLs to be revoked

const usePreview = computed(() => {
  if (props.preview === false) return false;

  return (
    !errorLoadImage.value &&
    !imageThumbnail.value?.toString().includes('data:image/png;base64') &&
    imageThumbnail.value !== config.value.placeholder
  );
});

const nextImage = (): void => {
  const images = props.galleries ?? [];
  currentGalleryImage.value = (currentGalleryImage.value + 1) % images.length;
  handleThumbnailClick();
};

const prevImage = (): void => {
  const images = props.galleries ?? [];

  currentGalleryImage.value =
    (currentGalleryImage.value - 1 + images.length) % images.length;

  handleThumbnailClick();
};

const startInterval = (): void => {
  stopInterval();

  if (props.galleries?.length) {
    intervalId.value = window.setInterval(() => {
      const images = props.galleries ?? [];
      currentGalleryImage.value =
        (currentGalleryImage.value + 1) % images.length;
    }, 3000);
  }
};

const stopInterval = (): void => {
  if (intervalId.value) {
    window.clearInterval(intervalId.value);
    intervalId.value = undefined;
  }
};

const imageSize = computed(() => {
  if (props.width != null) return props.width;
  else if (props.size === 'small') return 30;
  else if (props.size === 'medium') return 80;
  else if (props.useDefaultSize) return 125;
  return undefined;
});

const imageThumbnail = computedAsync(
  async (): Promise<string | Blob> => {
    if (!props.src || errorLoadImage.value) return config.value.placeholder;
    if (typeof props.src === 'string') {
      const urlOrObjectURL =
        props.src.includes('http') || // From public image or ObjectURL from Blob e.g 'blob:http://localhost:5173/ca48705d-1fc1-4a74-af38-353ffce7e02a'
        /^data:image\/[a-zA-Z0-9.+-]+;base64,/.test(props.src) // Thumbnail/placeholder
          ? props.src
          : await config.value.getImageURL?.(props.src, imageSize.value); // Get from server

      if (typeof urlOrObjectURL === 'string' && urlOrObjectURL.includes('blob'))
        objectURLs.push(urlOrObjectURL);

      return urlOrObjectURL || config.value.placeholder;
    }

    return props.src;
  },
  config.value.placeholder, // Show placeholder while fetching image
);

const imagePreview = shallowRef<string | Blob>();

/**
 * Since the getImageURL now supports Promise Data URI, the previous computed "imagePreview" will fetch image preview eagerly.
 * So, this function is usefull to load the preview image only when necesarry.
 */
const getImagePreviewUrl = async (): Promise<void> => {
  if (imagePreview.value != undefined) return;
  let preview = imageThumbnail.value;

  if (typeof preview === 'string') {
    const dummyPreviewUrl = config.value.getImageURL?.(
      'preview',
      undefined,
      undefined,
      true,
    );
    const imageOrigin =
      typeof dummyPreviewUrl == 'string' &&
      !dummyPreviewUrl?.includes('undefined')
        ? new URL(dummyPreviewUrl).origin
        : '';

    const isFromFiles =
      (imageOrigin && preview.includes(imageOrigin)) || // For getImageURL that return the image full url
      dummyPreviewUrl instanceof Promise; // For private image that uses fetch with Authorization may return Promise object

    preview =
      (preview.includes('http') && !isFromFiles) || // From public resource
      preview.includes('data:image/svg+xml') // Thumbnail
        ? preview
        : await config.value.getImageURL?.(props.src as string); // Get the full size for preview
  }

  if (typeof preview === 'string' && preview.includes('blob'))
    objectURLs.push(preview);

  imagePreview.value = preview || config.value.placeholder;
};

const handleThumbnailClick = (index?: number): void => {
  stopInterval();
  if (typeof index === 'number') currentGalleryImage.value = index;
  if (timeOutId.value) window.clearTimeout(timeOutId.value);
  else window.setTimeout(startInterval, 3000);
};

const onErrorLoadImage = (e: Event): void => {
  const src = (e.target as HTMLImageElement)?.src;

  if (
    src === imageThumbnail.value ||
    src.includes(imageThumbnail.value.toString())
  ) {
    errorLoadImage.value = true;
  }
};

const getGalleryImageSrc = (src?: string | Blob): string | undefined => {
  const image = src as string;

  if (typeof image === 'string') {
    return image.includes('http')
      ? image
      : config.value.getImageURL?.(image)?.toString(); // TODO: Fix this implementation to handle Promise data URI, dont forget to push the objectURL to objectURLs variable
  }

  return image;
};

watch(imageThumbnail, () => {
  imagePreview.value = undefined;
});
</script>

<template>
  <Image
    :image-class
    :image-style
    :preview="usePreview"
    :pt="{ root: $preset.image?.root({ props: $props }) }"
    :zoom-in-disabled
    :zoom-out-disabled
    @show="getImagePreviewUrl"
  >
    <template #image>
      <img
        v-bind="
          $preset.image?.image({
            props: $props,
            context: { imageSize: imageSize ?? thumbnailSize },
          })
        "
        :onerror="onErrorLoadImage"
        :src="imageThumbnail as unknown as string"
        alt=""
      />
    </template>

    <template #indicatoricon>
      <Icon v-bind="$preset.image.icon" icon="eye" />
    </template>

    <template #preview="slotProps">
      <template v-if="galleries?.length">
        <img
          :key="index"
          v-show="index === currentGalleryImage"
          v-for="({ src }, index) in galleries"
          :onerror="onErrorLoadImage"
          :src="getGalleryImageSrc(src)"
          :style="slotProps.style"
          alt=""
          data-wv-section="image-gallery"
        />

        <div
          @click.stop=""
          class="fixed bottom-0 left-0 flex justify-center items-center w-full p-4 mx-auto flex-row bg-black/80"
          data-wv-section="gallery-thumbnail"
        >
          <Button
            :class="[
              'text-surface-0 rotate-180 sticky left-0 !w-8 !h-8 [&_i]:w-5 [&_i]:h-5',
              'rounded-full mr-3',
            ]"
            @click="prevImage"
            aria-label="Previous Image"
            data-wv-section="prev-button"
            icon="arrow-right"
            icon-class="w-5 h-5"
            severity="secondary"
            text
          />

          <img
            :key="index"
            v-for="({ src }, index) in galleries"
            :class="[
              'h-10 w-10 object-cover cursor-pointer opacity-50 hover:opacity-100 transition-opacity',
              { '!opacity-100': currentGalleryImage == index },
            ]"
            :onerror="onErrorLoadImage"
            :src="getGalleryImageSrc(src)"
            :style="slotProps.style"
            @click.stop="handleThumbnailClick(index)"
            alt=""
            data-wv-section="thumbnail-image"
          />

          <Button
            :class="[
              'text-surface-0 sticky !w-8 !h-8 [&_i]:w-5 [&_i]:h-5',
              'rounded-full ml-3',
            ]"
            @click="nextImage"
            aria-label="Next Image"
            data-wv-section="next-button"
            icon="arrow-right"
            severity="secondary"
            text
          />
        </div>
      </template>

      <img
        v-else
        :onerror="onErrorLoadImage"
        :src="imagePreview as string"
        :style="slotProps.style"
        alt=""
      />
    </template>
  </Image>
</template>
